import { uiLogger } from "../../utils/logger";import { JSX, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon, UserCircleIcon, DocumentTextIcon, ChatBubbleLeftRightIcon, InformationCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../../stores/useAppStore';
import SimpleUserDropdown from '../ui/SimpleUserDropdown';
import { siteContent } from '../../data/content';
import { redirectToApp } from '../../utils/redirectToApp';
import { authApi } from '../../services/api';
import { authService } from '../../services/auth';

export default function Header(): JSX.Element {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { 
    isDarkMode, 
    toggleTheme, 
    openLoginModalClean, 
    openRegistrationModal,
    openEmailVerificationModal,
    pendingEmailVerification,
    isAuthenticated,
    user,
    navigationHistory,
    clearNavigationHistory
  } = useAppStore();

  const { navigation } = siteContent;
  const hideAuthButtons = import.meta.env.VITE_HIDE_AUTH_BUTTONS === 'true';

  const handleGetStarted = (): void => {
    if (pendingEmailVerification) {
      openEmailVerificationModal();
    } else {
      openRegistrationModal();
    }
  };

  const handleLogin = (): void => {
    if (pendingEmailVerification) {
      openEmailVerificationModal();
    } else {
      openLoginModalClean();
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    
    // Check if we should show back button (coming from legal pages)
    if (navigationHistory.shouldShowBackButton && navigationHistory.savedFormData) {
      uiLogger.debug('Back button clicked - returning to previous modal');
      
      // Navigate back to home
      navigate(navigationHistory.previousRoute || '/');
      
      // Open the appropriate modal based on saved data
      if (navigationHistory.savedFormData.modalType === 'registration') {
        openRegistrationModal();
      }
      
      // Don't clear navigation history yet - let the modal restore the data first
      return;
    }
    
    // Normal logo click behavior
    uiLogger.debug('Logo clicked - navigating to home');
    navigate('/');
  };

  const handleApp = async (): Promise<void> => {
    await redirectToApp();
    setMobileMenuOpen(false);
  };

  const handleContactUs = (): void => {
    try {
      const userRole = user?.role || 'User';
      
      if (userRole === 'Admin') {
        // Navigate to admin contact page for Admin users
        navigate('/admin/contact');
      } else {
        // Navigate to coming-soon page for regular users
        navigate('/coming-soon');
      }
      
      setMobileMenuOpen(false);
    } catch (error) {
      uiLogger.error('Navigation error:', error);
      // Fallback based on role
      const userRole = user?.role || 'User';
      if (userRole === 'Admin') {
        window.location.href = '/admin/contact';
      } else {
        window.location.href = '/coming-soon';
      }
    }
  };

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-50 transition-colors duration-300">
      <nav className="container-max section-padding" aria-label="Global">
        <div className="flex items-center justify-between py-4">
          {/* Left side: Logo and Navigation */}
          <div className="flex items-center lg:flex-1">
            {/* Logo / Back Button */}
            <button 
              onClick={handleLogoClick}
              className="-m-1.5 p-1.5 hover:opacity-80 transition-opacity duration-200 flex items-center mr-8"
            >
              {navigationHistory.shouldShowBackButton ? (
                <div className="flex items-center space-x-2">
                  <XMarkIcon className="h-6 w-6 text-gray-900 dark:text-white" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Close</span>
                </div>
              ) : (
                <span className="text-2xl font-bold text-gradient">
                  {navigation.logo}
                </span>
              )}
            </button>

            {/* Desktop navigation */}
            <div className="hidden lg:flex lg:gap-x-8">
              {navigation.links.map((item) => (
                item.href.startsWith('#') ? (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-sm font-medium leading-6 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                ) : (
                  <button
                    key={item.name}
                    onClick={() => navigate(item.href)}
                    className="text-sm font-medium leading-6 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    {item.name}
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              data-testid="mobile-menu-button"
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-300"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Right side actions */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-4">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="btn-ghost p-2"
              aria-label="Toggle theme"
            >
              {isDarkMode ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>

            {/* Authentication State */}
            {!hideAuthButtons && (
              <>
                {isAuthenticated ? (
                  /* Show user account dropdown when authenticated */
                  <SimpleUserDropdown />
                ) : (
                  /* Show login/register buttons when not authenticated */
                  <>
                    <button
                      data-testid="login-button"
                      onClick={handleLogin}
                      className="text-sm font-medium leading-6 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                    >
                      {navigation.cta.login}
                    </button>

                    <button
                      data-testid="get-started-button"
                      onClick={handleGetStarted}
                      className="btn-primary"
                    >
                      {navigation.cta.getStarted}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-50" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:bg-gray-900 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 dark:sm:ring-gray-100/10">
          <div className="flex items-center justify-between">
            <button 
              onClick={handleLogoClick}
              className="-m-1.5 p-1.5 hover:opacity-80 transition-opacity duration-200 flex items-center"
            >
              {navigationHistory.shouldShowBackButton ? (
                <div className="flex items-center space-x-2">
                  <XMarkIcon className="h-5 w-5 text-gray-900 dark:text-white" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Close</span>
                </div>
              ) : (
                <span className="text-xl font-bold text-gradient">
                  {navigation.logo}
                </span>
              )}
            </button>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-gray-300"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10 dark:divide-gray-500/25">
              {/* Navigation links */}
              <div className="space-y-2 py-6">
                {navigation.links.map((item) => (
                  item.href.startsWith('#') ? (
                    <a
                      key={item.name}
                      href={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-medium leading-7 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <button
                      key={item.name}
                      onClick={() => {
                        navigate(item.href);
                        setMobileMenuOpen(false);
                      }}
                      className="-mx-3 block w-full text-left rounded-lg px-3 py-2 text-base font-medium leading-7 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      {item.name}
                    </button>
                  )
                ))}
              </div>
              <div className="py-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Theme
                  </span>
                  <button
                    onClick={toggleTheme}
                    className="btn-ghost p-2"
                    aria-label="Toggle theme"
                  >
                    {isDarkMode ? (
                      <SunIcon className="h-5 w-5" />
                    ) : (
                      <MoonIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {/* Mobile Menu Items - Contact Us and API Gateway */}
                {isAuthenticated && (
                  <div className="space-y-2">
                    {/* App - Available for all authenticated users */}
                    <button
                      onClick={handleApp}
                      className="flex items-center w-full text-left text-base font-medium leading-7 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-3 py-2"
                    >
                      <RocketLaunchIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                      App
                    </button>

                    {/* Contact Us - Available for all authenticated users */}
                    <button
                      onClick={handleContactUs}
                      className="flex items-center w-full text-left text-base font-medium leading-7 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-3 py-2"
                    >
                      {user?.role === 'Admin' ? (
                        <ChatBubbleLeftRightIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                      ) : (
                        <InformationCircleIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                      )}
                      Contact Us
                    </button>
                  </div>
                )}

                {/* Mobile Authentication State */}
                {!hideAuthButtons && (
                  <>
                    {isAuthenticated ? (
                      /* Show user info and logout in mobile */
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {user?.useIcon ? (
                              <UserCircleIcon className="h-6 w-6" />
                            ) : (
                              user?.initials || (user?.name ? user.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) : 'U')
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {user?.name || 'Demo User'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user?.email || 'demo@fabrik28.ai'}
                            </p>
                          </div>
                        </div>
                        
                        <button
                          onClick={async (): Promise<void> => {
                            try {
                              // Sign out from Cognito and clear session
                              await authApi.logout();
                              
                              // Update local state
                              useAppStore.getState().logout();
                              setMobileMenuOpen(false);
                              
                              uiLogger.info('✅ User logged out successfully');
                            } catch (error) {
                              uiLogger.error('❌ Logout error:', error);
                              
                              // Even if remote logout fails, clear local state
                              useAppStore.getState().logout();
                              setMobileMenuOpen(false);
                            }
                          }}
                          className="block w-full text-left text-base font-medium leading-7 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-3 py-2"
                        >
                          Sign out
                        </button>
                      </div>
                    ) : (
                      /* Show login/register buttons in mobile */
                      <>
                        <button
                          onClick={(): void => {
                            handleLogin();
                            setMobileMenuOpen(false);
                          }}
                          className="block w-full text-left text-base font-medium leading-7 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-3 py-2"
                        >
                          {navigation.cta.login}
                        </button>
                        <button
                          onClick={(): void => {
                            handleGetStarted();
                            setMobileMenuOpen(false);
                          }}
                          className="btn-primary w-full"
                        >
                          {navigation.cta.getStarted}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
